import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, ShoppingBag, Trash2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useApp } from '@/context/AppContext';

const CartDrawer = () => {
  const { isCartOpen, setIsCartOpen, cart, removeFromCart, updateCartQuantity, cartTotal } = useApp();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed top-0 right-0 z-50 h-full w-full max-w-md bg-gradient-burgundy-soft border-l border-border/50 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border/30">
              <div className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 text-silver" />
                <h2 className="font-display text-lg text-silver-light">Your Cart</h2>
                <span className="text-sm text-muted-foreground">({cart.length} items)</span>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setIsCartOpen(false)}
                className="text-silver hover:text-silver-light"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-luxury">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <div className="h-16 w-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                    <ShoppingBag className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground font-body mb-4">
                    Your cart is empty
                  </p>
                  <Link to="/products" onClick={() => setIsCartOpen(false)}>
                    <Button variant="silver">
                      Start Shopping
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              ) : (
                cart.map((item, index) => (
                  <motion.div
                    key={`${item.id}-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex gap-4 p-3 bg-card rounded-xl border border-border/50"
                  >
                    {/* Product Image */}
                    <div className="h-20 w-20 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                      <img
                        src={item.images?.[0]}
                        alt={item.name}
                        className="h-full w-full object-cover"
                      />
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-sm text-silver-light truncate">
                        {item.name}
                      </h3>
                      {item.customization && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          Customized
                        </p>
                      )}
                      <p className="text-sm font-medium text-ivory mt-1">
                        {formatPrice(item.price)}
                      </p>

                      {/* Quantity Controls */}
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateCartQuantity(item.id, item.quantity - 1, item.customization)}
                            className="h-6 w-6 rounded border border-border flex items-center justify-center text-muted-foreground hover:text-silver hover:border-silver/50 transition-colors"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="text-sm font-medium text-foreground min-w-[20px] text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateCartQuantity(item.id, item.quantity + 1, item.customization)}
                            className="h-6 w-6 rounded border border-border flex items-center justify-center text-muted-foreground hover:text-silver hover:border-silver/50 transition-colors"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>

                        <button
                          onClick={() => removeFromCart(item.id, item.customization)}
                          className="text-muted-foreground hover:text-destructive transition-colors p-1"
                          aria-label="Remove item"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Footer */}
            {cart.length > 0 && (
              <div className="p-4 border-t border-border/30 space-y-4">
                {/* Subtotal */}
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-display text-lg text-silver-light">
                    {formatPrice(cartTotal)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Shipping calculated at checkout
                </p>

                {/* Actions */}
                <div className="space-y-2">
                  <Link to="/cart" onClick={() => setIsCartOpen(false)} className="block">
                    <Button variant="ivory" className="w-full">
                      View Cart
                    </Button>
                  </Link>
                  <Button
                    variant="premium"
                    className="w-full"
                    onClick={() => {
                      const message = encodeURIComponent(
                        `Assalamu Alaikum! I would like to checkout my cart:\n\n${cart.map(item => `- ${item.name} x${item.quantity}`).join('\n')}\n\nTotal: ${formatPrice(cartTotal)}`
                      );
                      window.open(`https://wa.me/919111841033?text=${message}`, '_blank');
                    }}
                  >
                    Checkout via WhatsApp
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
